/**
 * Centralized API client for making HTTP requests
 */
import { API_BASE_URL, TIMEOUTS, HEADERS, CONTENT_TYPES } from '../constants/api';

class ApiClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.defaultTimeout = TIMEOUTS.DEFAULT;
  }

  /**
   * Get authorization header
   * @returns {Object} Authorization header
   */
  getAuthHeader() {
    const token = localStorage.getItem('token');
    return token ? { [HEADERS.AUTHORIZATION]: `Bearer ${token}` } : {};
  }

  /**
   * Get default headers
   * @returns {Object} Default headers
   */
  getDefaultHeaders() {
    return {
      [HEADERS.CONTENT_TYPE]: CONTENT_TYPES.JSON,
      [HEADERS.ACCEPT]: CONTENT_TYPES.JSON,
      ...this.getAuthHeader()
    };
  }

  /**
   * Handle API response
   * @param {Response} response - Fetch response
   * @param {string} endpoint - The endpoint that was called
   * @returns {Promise} Parsed response data
   */
  async handleResponse(response, endpoint) {
    const contentType = response.headers.get(HEADERS.CONTENT_TYPE);
    
    let data;
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      // Handle 401 Unauthorized - Invalid or expired token
      if (response.status === 401) {
        // Check if this is a public endpoint - don't logout for these
        const publicEndpoints = [
          '/auth/login',
          '/auth/register',
          '/auth/verify-email',
          '/auth/forgot-password',
          '/auth/reset-password',
          '/auth/setup-password',
          '/cms',
          '/flights/search',
          '/airports',
          '/countries',
          '/payments/paypal/success'  // Don't auto-logout on payment callback — handled by PaymentCallbackPage
        ];
        
        const isPublicEndpoint = publicEndpoints.some(ep => endpoint.includes(ep));
        
        if (!isPublicEndpoint) {
          const message = typeof data === 'object' ? data.message : data;
          if (message && (message.includes('Invalid or expired token') || message.includes('Unauthorized'))) {
            // Import logout utility dynamically to avoid circular dependencies
            import('../utils/logout').then(({ performSimpleLogout }) => {
              performSimpleLogout();
            });
          }
        }
      }

      const error = new Error(data.message || `HTTP ${response.status}`);
      error.status = response.status;
      error.data = data;
      throw error;
    }

    return data;
  }

  /**
   * Make HTTP request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      timeout: this.defaultTimeout,
      headers: this.getDefaultHeaders(),
      ...options
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), config.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
        credentials: 'include'
      });

      clearTimeout(timeoutId);
      return await this.handleResponse(response, endpoint);
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async get(endpoint, params = {}, options = {}) {
    const queryString = new URLSearchParams(params).toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    return this.request(url, {
      method: 'GET',
      ...options
    });
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {*} data - Request body data
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async post(endpoint, data = null, options = {}) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'POST',
      body: isFormData ? data : (data ? JSON.stringify(data) : null),
      ...(isFormData ? { headers: { ...this.getAuthHeader(), [HEADERS.ACCEPT]: CONTENT_TYPES.JSON } } : {}),
      ...options
    });
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {*} data - Request body data
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async put(endpoint, data = null, options = {}) {
    const isFormData = data instanceof FormData;
    return this.request(endpoint, {
      method: 'PUT',
      body: isFormData ? data : (data ? JSON.stringify(data) : null),
      ...(isFormData ? { headers: { ...this.getAuthHeader(), [HEADERS.ACCEPT]: CONTENT_TYPES.JSON } } : {}),
      ...options
    });
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {*} data - Request body data
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async patch(endpoint, data = null, options = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : null,
      ...options
    });
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options
    });
  }

  /**
   * Upload file
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with file
   * @param {Object} options - Request options
   * @returns {Promise} Response data
   */
  async upload(endpoint, formData, options = {}) {
    const headers = { ...this.getAuthHeader() };
    // Don't set Content-Type for FormData, let browser set it with boundary
    
    return this.request(endpoint, {
      method: 'POST',
      body: formData,
      headers,
      timeout: TIMEOUTS.UPLOAD,
      ...options
    });
  }
}

// Create and export singleton instance
const apiClient = new ApiClient();
export default apiClient;